/* ─── IY-SP branded forms — schemas ───────────────────────────
   Replaces the Google Forms (intake, pre-session, post-session,
   celebration potluck). Questions copied field-for-field from the
   originals; only spelling fixes and section grouping added.

   Item types:
     short | paragraph | date
     radio    { options, other? }        — one choice
     checkbox { options, other? }        — many choices
     scale    { options, low?, high? }   — numbered buttons
     grid     { rows, cols }             — one choice per row
     signature                           — drawn signature, PNG data URL
   `required: true` is enforced client-side; the server only checks
   name (+ email when `email: "required"`).
──────────────────────────────────────────────────────────────── */

const NEVER_ALWAYS     = ["Never", "Rarely", "Sometimes", "Often", "Always"];
const NEVER_USUALLY    = ["Never", "Rarely", "Sometimes", "Usually", "Always"];
const NOT_AT_ALL       = ["Not at all", "A little bit", "Somewhat", "Quite a bit", "Very much"];
const DIFFICULTY       = ["Without any difficulty", "With a little difficulty", "With some difficulty", "With much difficulty", "Unable to do"];
const LIMIT            = ["Not at all", "Very little", "Somewhat", "Quite a lot", "Cannot do"];
const CONFIDENCE       = ["I am not at all confident", "I am a little confident", "I am somewhat confident", "I am quite confident", "I am very confident"];
const ONE_TO_SEVEN     = ["1", "2", "3", "4", "5", "6", "7"];
const FEEL_SCALE_HELP  = "1 excellent · 2 very good · 3 good · 4 neither · 5 bad · 6 very bad · 7 extremely bad";

const SYMPTOMS = [
  "Mental Fatigue", "Physical Fatigue", "Difficulty Breathing", "Low Back Discomfort",
  "Heart Palpitations", "Chest Pain", "Dizziness", "Migraine/Headache",
  "Neck/Shoulder Discomfort", "General Muscle/Body Aches", "Diarrhea", "Constipation",
  "Nausea", "Heartburn", "Anxiety", "Depression", "Knee Discomfort",
  "Cough/Sore Throat/Phlegm", "Hip Discomfort",
];

const radios = (prefix, questions, options) =>
  questions.map((q, i) => ({ id: `${prefix}${i + 1}`, type: "radio", q, options }));

export const FORMS = {
  /* ───────────────────────── Intake ───────────────────────── */
  intake: {
    eyebrow: "Yoga Therapy",
    title: "Student Intake Form",
    description:
      "This survey gives us information about your health so your therapy can be tailored to your needs. " +
      "Your responses are also used to track your improvement over time and adjust your yoga therapy sequence as needed. " +
      "It takes about 30–40 minutes. Your answers are saved on this device as you go, so you can come back and finish later. " +
      "We'll review your responses before your intake interview. Questions? Email info@iy-sp.com.",
    email: "required",
    sections: [
      {
        title: "About you",
        items: [
          { id: "age", type: "short", q: "Age" },
          { id: "occupation", type: "paragraph", q: "What is your occupation?" },
        ],
      },
      {
        title: "Your practice",
        items: [
          { id: "newStudent", type: "radio", q: "Are you a new Iyengar yoga student?", options: ["Yes", "No"], other: true },
          { id: "practiceIyengar", type: "radio", q: "Do you practice Iyengar Yoga?", options: ["Yes", "No"] },
          { id: "practiceDays", type: "scale", q: "If yes, how often?", options: ONE_TO_SEVEN, high: "Days a week" },
          { id: "practiceLength", type: "radio", q: "And for how long on average?", options: ["15 min", "30 min", "1 hr"], other: true },
          { id: "yearsIyengar", type: "paragraph", q: "If applicable, how many years or months have you practiced Iyengar yoga?" },
          { id: "yearsOther", type: "paragraph", q: "If applicable, how many years or months have you practiced other forms of yoga? (Please describe)" },
          { id: "days7", type: "radio", q: "In the past 7 days, how many days have you practiced yoga?",
            options: ["Every day (100%)", "Most days (75%)", "About 3-4 days (50%)", "A few days (25%)", "None (0%)"], other: true },
          { id: "minutesAvg", type: "paragraph", q: "On average, each time you practice yoga how many minutes are you practicing?" },
        ],
      },
      {
        title: "Your health",
        items: [
          { id: "concerns", type: "checkbox", q: "Please check all the areas of concern regarding your health.", other: true, columns: true,
            options: ["Allergies", "Asthma", "Ankles/Feet", "Anxiety", "Arthritis", "Auto-immune dysfunction", "Bladder", "Brain",
              "Carpal Tunnel", "Cancer", "Chronic Fatigue", "Diabetes", "Depression", "Dizziness", "Eyes",
              "Gastrointestinal Disorder", "Headache", "Heart Condition", "Heel Spur", "High Blood Pressure", "Hips/Legs",
              "HIV-related", "Hypoglycemia", "Insomnia", "Kidney", "Knees", "Liver", "Lower Back", "Low Blood Pressure",
              "Menopausal", "Menstrual Problems", "Multiple Sclerosis", "Neck", "Osteoporosis", "Plantar fasciitis",
              "Pregnancy", "Post-Partum", "Prolonged illness", "Prostate", "Recent Surgery", "Sedentary Lifestyle",
              "Sciatica", "Scoliosis", "Shoulders", "Thyroid", "Upper Back", "Wrist/Hand"] },
          { id: "chiefConcern", type: "short", q: "Please list your chief or top concern/complaint" },
          { id: "conditions", type: "paragraph", q: "Please describe your conditions in detail (including diagnosis if applicable)" },
          { id: "injury", type: "paragraph", q: "Have you ever been in a car accident or had a traumatic injury? If yes, please list the year of this injury and describe the resulting injuries." },
          { id: "treatments", type: "checkbox", q: "Please select any of the following treatments that you have previously used or are currently using:", other: true,
            options: ["Physical Therapy", "Psychotherapy", "Chiropractic", "Massage", "Acupuncture", "Deep Tissue Therapy", "Homeopathic Medicine"] },
          { id: "medications", type: "paragraph",
            q: "Please list any medications or supplements that you are currently taking with dosage. Please include any over the counter medication as well as vitamins.",
            help: "Examples: 0.3 mg of melatonin nightly; 20 mg lisinopril daily; estradiol (oral 1 mg/day)" },
          { id: "gender", type: "radio", q: "Gender: How do you identify?", options: ["Female", "Male", "Nonbinary", "Prefer not to say"], other: true },
          { id: "ethnicity", type: "checkbox", q: "What is your ethnicity/race? (Please select all that apply)", other: true,
            options: ["American Native Indian or Alaskan Native", "Asian", "Black", "Native Hawaiian/Pacific Islander",
              "White/Caucasian", "Hispanic/Latino/a", "Arab/Middle Eastern", "Prefer not to say"] },
        ],
      },
      {
        title: "Physical health & pain",
        items: [
          { id: "physicalHealth", type: "scale", q: "In general, how would you rate your physical health?", options: ["1", "2", "3", "4", "5"], low: "Poor", high: "Excellent" },
          { id: "everyday", type: "radio", q: "To what extent are you able to carry out your everyday physical activities such as walking, climbing stairs, carrying groceries, or moving a chair?",
            options: ["Completely", "Mostly", "Moderately", "A little", "Not at all"] },
          { id: "painAvg", type: "scale", q: "In the past 7 days, how would you rate your pain on average?",
            options: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"], low: "No pain", high: "Worst imaginable pain" },
          ...radios("pain", [
            "In the past 7 days, how often did you have pain so bad that you could not do anything for the whole day?",
            "In the past 7 days, how often did you have pain so bad that you could not get out of bed?",
            "In the past 7 days, how often did you have very severe pain?",
            "In the past 7 days, how often did you have pain so bad that you had to stop what you were doing?",
            "In the past 7 days, how often did you have pain so bad that it was hard to finish what you were doing?",
          ], NEVER_ALWAYS),
          ...radios("able", [
            "Are you able to do chores such as vacuuming or yard work?",
            "Are you able to go up and down stairs at a normal pace?",
            "Are you able to go for a walk of at least 15 minutes?",
            "Are you able to run errands and shop?",
          ], DIFFICULTY),
          ...radios("limit", [
            "Does your health now limit you in doing two hours of physical labor?",
            "Does your health now limit you in doing moderate work around the house like vacuuming, sweeping floors or carrying in groceries?",
            "Does your health now limit you in lifting or carrying groceries?",
            "Does your health now limit you in doing heavy work around the house like scrubbing floors, or lifting or moving heavy furniture?",
          ], LIMIT),
        ],
      },
      {
        title: "Managing your symptoms",
        items: radios("conf", [
          "I can manage my symptoms during my daily activities",
          "I can keep my symptoms from interfering with relationships with friends and family",
          "I can manage my symptoms in a public place",
          "I can work with my doctor to manage my symptoms",
        ], CONFIDENCE),
      },
      {
        title: "Your health in daily life",
        items: [
          { id: "rely30", type: "radio", q: "In the past 30 days, how much did you rely on others to take care of you because of your health?", options: NOT_AT_ALL },
          ...radios("slow", [
            "In the past 30 days, how often did your health slow you down?",
            "In the past 30 days, how often did your health make it hard for you to do things?",
            "In the past 30 days, how often did your health keep you from going out?",
          ], NEVER_ALWAYS),
          { id: "friends30", type: "radio", q: "In the past 30 days, how much did your health make it hard for you to do things with your friends?", options: NOT_AT_ALL },
        ],
      },
      {
        title: "How you've been feeling",
        items: radios("mood", [
          "In the past 7 days, I felt worthless",
          "In the past 7 days, I felt helpless",
          "In the past 7 days, I felt depressed",
          "In the past 7 days, I felt hopeless",
          "In the past 7 days, I felt fearful",
          "In the past 7 days, I found it hard to focus on anything other than my anxiety",
          "In the past 7 days, my worries overwhelmed me",
          "In the past 7 days, I felt uneasy",
        ], NEVER_ALWAYS),
      },
      {
        title: "Connection",
        items: radios("social", [
          "I felt left out",
          "I feel that people barely know me",
          "I feel isolated from others",
          "I feel that people are around me but not with me",
        ], NEVER_USUALLY),
      },
      {
        title: "Sleep & energy",
        items: [
          { id: "sleepQuality", type: "radio", q: "In the past 7 days my sleep quality was", options: ["Very poor", "Poor", "Fair", "Good", "Very good"], other: true },
          ...radios("sleep", [
            "In the past 7 days, my sleep was refreshing",
            "In the past 7 days, I had a problem with my sleep",
            "In the past 7 days, I had difficulty falling asleep",
            "During the past 7 days, I felt fatigued",
            "During the past 7 days, I have trouble starting things because I am tired",
            "In the past 7 days, how run-down did you feel on average",
            "How fatigued were you on average?",
          ], NOT_AT_ALL),
        ],
      },
    ],
  },

  /* ──────────────────────── Pre-session ──────────────────────── */
  "pre-session": {
    eyebrow: "Yoga Therapy",
    title: "Pre-Session Form",
    description: "To be completed before each therapy session. Please read the instructions carefully.",
    email: "required",
    sections: [
      {
        title: "Today",
        items: [
          { id: "date", type: "date", q: "Today's date", required: true, defaultToday: true },
          { id: "daysPracticed", type: "scale", q: "Number of days practiced during the previous week", options: ONE_TO_SEVEN },
          { id: "practiceTime", type: "radio", q: "Average practice time in minutes",
            options: ["15 min", "30 min", "45 min", "60 min", "90 min", "more than 90 mins", "NONE"] },
          { id: "lifeEvents", type: "paragraph",
            q: "Describe significant health related or life events that could affect my practice today",
            help: "e.g. trouble sleeping, bereavement, stress, trauma, mental health issues — this list is not exhaustive. If none, leave blank." },
        ],
      },
      {
        title: "Symptoms",
        items: [
          { id: "symptoms", type: "grid", q: "Check the severity of the symptoms you have today",
            help: "1 mild – 7 severe. Leave a row blank if you don't have that symptom.", rows: SYMPTOMS, cols: ONE_TO_SEVEN },
          { id: "otherSymptoms", type: "paragraph", q: "Please describe any other debilitating symptoms and rate the severity (1 mild – 7 severe). If none, leave blank." },
          { id: "menses", type: "checkbox", q: "For those who are menstruating, please check all that apply:",
            options: ["Normal Healthy Menses", "Cramps", "Low Back Ache", "Heavy Flow", "Scanty Flow"] },
        ],
      },
      {
        title: "How you feel before class",
        items: [
          { id: "physical", type: "scale", q: "Overall, how do you feel physically before the class?", help: FEEL_SCALE_HELP,
            options: ONE_TO_SEVEN, low: "Good", high: "Bad", required: true },
          { id: "emotional", type: "scale", q: "Overall, how do you feel emotionally before the class?", help: FEEL_SCALE_HELP,
            options: ONE_TO_SEVEN, low: "Good", high: "Bad", required: true },
        ],
      },
    ],
  },

  /* ──────────────────────── Post-session ──────────────────────── */
  "post-session": {
    eyebrow: "Yoga Therapy",
    title: "Post-Session Form",
    description: "To be completed after each therapy session. Please read the instructions carefully.",
    email: "required",
    sections: [
      {
        title: "Today",
        items: [{ id: "date", type: "date", q: "Today's date", required: true, defaultToday: true }],
      },
      {
        title: "Symptoms now",
        items: [
          { id: "symptoms", type: "grid", q: "If you checked any symptoms before class, please check the severity of each symptom now.",
            help: "0 none – 7 severe. Leave blank if you checked none on the pre-session form.",
            rows: SYMPTOMS, cols: ["0", ...ONE_TO_SEVEN] },
          { id: "menses", type: "grid", q: "For those who are menstruating: if you checked any of these before class, how did the practice help?",
            rows: ["Menstrual Cramps", "Low Back Ache", "Heavy Flow", "Scanty Flow"], cols: ["No Change", "Improved", "Resolved"] },
          { id: "otherSymptoms", type: "paragraph", q: "If you listed other symptoms on your pre-session form, list them here and rate current severity (0–7):" },
        ],
      },
      {
        title: "How you feel after class",
        items: [
          { id: "physical", type: "scale", q: "Overall, how do you feel physically after the class?", help: FEEL_SCALE_HELP,
            options: ONE_TO_SEVEN, low: "Good", high: "Bad", required: true },
          { id: "emotional", type: "scale", q: "Overall, how do you feel emotionally after the class?", help: FEEL_SCALE_HELP,
            options: ONE_TO_SEVEN, low: "Good", high: "Bad", required: true },
          { id: "difficulty", type: "scale", q: "Overall, how easy or difficult did you find the class today?",
            options: ONE_TO_SEVEN, low: "Easy", high: "Difficult", required: true },
          { id: "comments", type: "paragraph", q: "Any other comments about your experience in the class today" },
        ],
      },
    ],
  },

  /* ─────────────── Therapy Intensive 2026 consent ─────────────── */
  "therapy-intensive-consent": {
    eyebrow: "Therapy Intensive 2026",
    title: "Case Study Consent",
    description:
      "Please fill out this form to indicate your preference for allowing us to store your case study in our archive. " +
      "Your information will not be available to the public. With approval from Tricia Amheiser, select certified Iyengar Yoga teachers (CIYTs) " +
      "will be given limited permission to view your case study for educational purposes. No CIYTs will be given permission to download any case study.",
    email: "required",
    sections: [
      {
        title: "Your case study",
        items: [
          { id: "date", type: "date", q: "Today's date", required: true, defaultToday: true },
        ],
      },
      {
        title: "Consent",
        items: [
          { id: "consent", type: "radio", q: "I consent to release my information as written in the case study.",
            help: "I understand this includes medical information gathered in the pre-therapy intake survey, photos of me taken in therapy sessions, " +
              "and notes and observations made by my teacher(s) during therapy sessions. " +
              "I understand that only select CIYTs will view my case study for educational purposes, and only with permission from Tricia Amheiser. " +
              "I consent to release this information in perpetuity.",
            options: ["Yes", "No"], required: true },
          { id: "censorship", type: "checkbox",
            q: "If you answered Yes above, please indicate whether you would prefer to have censorship of your identifying information.",
            help: "You may select more than one box if applicable.",
            options: [
              "My case study may be used as it is, with my first name and photos.",
              "I prefer to have my name changed to my initials.",
              "I prefer to have my name changed to a random alternative.",
              "I prefer to have my face blurred in the photos.",
            ], other: true },
        ],
      },
      {
        title: "Anything else",
        items: [
          { id: "comments", type: "paragraph", q: "Any additional comments, questions or concerns",
            help: "You may also email info@iy-sp.com." },
          { id: "signature", type: "signature", q: "Signature", required: true },
        ],
      },
    ],
  },

  /* ──────────────────────── Celebration ──────────────────────── */
  celebration: {
    eyebrow: "Sunday, October 18",
    title: "Flourish Opening Celebration",
    description: "Potluck sign-up. We just want to know how many people to expect and what you're planning to bring.",
    email: "optional",
    sections: [
      {
        title: "Your RSVP",
        items: [
          { id: "partySize", type: "scale", q: "How many people are coming?", options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"], required: true },
          { id: "dish", type: "short", q: "What vegetarian or vegan dish are you bringing?", help: "No more fruit salad, please and thank you!" },
        ],
      },
    ],
  },
};

export const FORM_SLUGS = Object.keys(FORMS);
